import {catchError, map, mergeMap, take} from 'rxjs/operators';
import {Component, OnDestroy, OnInit, ViewChild} from '@angular/core';
import {ProgressComponent} from '../progress/progress.component';
import {Subscription, throwError} from 'rxjs';
import {MatPaginator} from '@angular/material/paginator';
import {MatSnackBar} from '@angular/material/snack-bar';
import {MatSort} from '@angular/material/sort';
import {
    MatCell,
    MatCellDef,
    MatColumnDef,
    MatHeaderCell,
    MatHeaderCellDef,
    MatHeaderRow,
    MatHeaderRowDef,
    MatRow,
    MatRowDef,
    MatTable,
    MatTableDataSource
} from '@angular/material/table';
import {ActivatedRoute, RouterLink} from '@angular/router';
import {BreadCrumb} from '../../models/breadcrumb';
import {User} from '../../models/user';
import {UsersService} from '../../services/users.service';
import {Auth, authState} from '@angular/fire/auth';
import {BreadcrumbsComponent} from '../breadcrumbs/breadcrumbs.component';
import {MatCard, MatCardHeader, MatCardTitle} from '@angular/material/card';
import {MatButton, MatMiniFabButton} from '@angular/material/button';
import {I18nPluralPipe} from '@angular/common';
import {MatError} from '@angular/material/input';
import {collection, collectionData, DocumentData, Firestore, orderBy, query, where} from '@angular/fire/firestore';

@Component({
    selector: 'app-account-users',
    templateUrl: './account-users.component.html',
    styleUrls: ['./account-users.component.css'],
    imports: [
        ProgressComponent,
        BreadcrumbsComponent,
        MatCard,
        MatCardTitle,
        MatCardHeader,
        RouterLink,
        MatButton,
        I18nPluralPipe,
        MatError,
        MatTable,
        MatColumnDef,
        MatHeaderCellDef,
        MatHeaderCell,
        MatCellDef,
        MatCell,
        MatMiniFabButton,
        MatHeaderRowDef,
        MatHeaderRow,
        MatRowDef,
        MatRow,
        MatSort
    ],
    standalone: true
})
export class AccountUsersComponent implements OnInit, OnDestroy {
    public displayedColumns = [
        'email',
        'role',
        'actions'
    ];
    public generalErrorMessage = '';
    userPluralMapping = {
        '=0': '0 Users',
        '=1': '1 User',
        other: '# Users'
    };
    dataSource: MatTableDataSource<DocumentData>;
    breadcrumbs: BreadCrumb[];
    @ViewChild(ProgressComponent, {static: true}) progress: ProgressComponent;
    @ViewChild(MatPaginator) paginator: MatPaginator;
    @ViewChild(MatSort, {static: true}) sort: MatSort;
    private userId: string;
    admin = false;
    private authStateSubscription: Subscription;
    private subscription: Subscription;
    public accountId: any;

    constructor(private firestore: Firestore,
                private route: ActivatedRoute,
                private userService: UsersService,
                private fireAuth: Auth,
                private snackbar: MatSnackBar) {
    }

    ngOnInit() {
        this.authStateSubscription = authState(this.fireAuth).pipe(
            take(1),
            mergeMap((user) => {
                this.userId = user.uid;
                // We don't refresh the token here because it will get called often
                return user.getIdTokenResult();
            }),
        ).subscribe(token => {
            this.admin = !!token.claims.admin;
        });

        this.route.paramMap.subscribe(params => {
            this.accountId = params.get('accountId');
            this.breadcrumbs = [
                {url: ['/'], title: 'Accounts'},
                {url: null, title: 'Users'},
            ];

            const usersColRef = collection(this.firestore, 'users');
            const usersQuery = query(
                usersColRef,
                where('accountId', '==', this.accountId),
                orderBy('email', 'asc')
            );

            this.subscription = collectionData(usersQuery, {idField: 'id'}).pipe(
                catchError(e => throwError(() => new Error(e)))
            ).subscribe({
                next: data => {
                    this.dataSource = new MatTableDataSource(data);
                    this.dataSource.sort = this.sort;
                    this.dataSource.paginator = this.paginator;
                },
                error: e => {
                    console.log(e);
                }
            });
        });
    }

    ngOnDestroy(): void {
        this.subscription.unsubscribe();
        this.authStateSubscription.unsubscribe();
    }

    search(query: string) {
        this.dataSource.filter = query.trim().toLowerCase();
    }

    deleteUser(user: User) {
        if (this.userId === user.id) {
            this.snackbar.open('You can\'t delete your own account', null, {duration: 3000});
            return;
        }
        if (this.admin) {
            this.progress.setVisible(true);
            this.userService.deleteUser(user.id).pipe(
                map(() => {
                    return;
                }),
                catchError((error: Response | any) => {
                    return throwError(() => new Error(error));
                })).subscribe({
                next: () => {
                    this.progress.setVisible(false);
                }, error: error => {
                    this.progress.setVisible(false);
                    this.generalErrorMessage = error.toString();
                }
            });
        } else {
            this.snackbar.open('Permission denied. Please contact an administrator', null, {duration: 3000});
        }
    }
}
